<script>
import { scrollLeft, useInteractionTracker } from '../../services/utils';
import SkokkaCarouselControls from "./carousel-controls.vue";

import { slideCloneClass } from '../post/gallery/gallery.vue';

export default {
    name: "SkokkaCarousel",
    components: {
        SkokkaCarouselControls,
    },
    props: {
        infinite: {
            type: Boolean,
            default: false
        },
        auto: {
            type: Boolean,
            default: false,
        },
        autoSlideDelay: {
            type: Number,
            default: 3000
        },
        trackClassName: {
            type: String,
            default: "",
        }
    },
    data() {
        return {
            isScrolling: false,
            currentSlideIndex: 0,
            stopScrollingTimeout: undefined,
            autoSlideTimeout: undefined,
            userInteractionTracker: useInteractionTracker(),
            numberOfSlides: 0
        };
    },
    methods: {
        initInfiniteSlide() {
            if (!this.infinite) {
                return;
            }

            const track = this.getTrack();
            const slides = this.getSlides();

            if (slides.length < 2) {
                return;
            }

            const first = slides[0].cloneNode(true);
            const last = slides[slides.length - 1].cloneNode(true);

            first.classList.add(slideCloneClass);
            last.classList.add(slideCloneClass);

            // Fix baguetteBox issue with cloned slides
            // It will ignore slides with given class put on a tag (https://github.com/feimosi/baguetteBox.js/blob/dev/README.md)
            if (first.firstElementChild) {
                first.firstElementChild.classList.add(slideCloneClass);
            } else {
                console.warn("First cloned slide does not have an element child.");
            }       

            if (last.firstElementChild) {
                last.firstElementChild.classList.add(slideCloneClass);
            } else {
               console.warn("Last cloned slide does not have an element child.");
            }

            track.appendChild(first);
            track.insertBefore(last, slides[0]);

            this.currentSlideIndex = 1;

            this.goToSlide(this.currentSlideIndex, "instant");
        },
        initAutoSlide() {
            if (!this.auto) {
                return;
            }
            this.clearAutoSlideTimeout();
            this.autoSlideTimeout = setTimeout(() => {
                this.goToSlide(
                    this.currentSlideIndex >= this.numberOfSlides - 1
                        ? 0
                        : this.currentSlideIndex + 1
                );
            }, this.autoSlideDelay);

        },
        clearAutoSlideTimeout() {
            if (this.autoSlideTimeout) {
                clearTimeout(this.autoSlideTimeout);
            }
        },
        clearStopScrollingTimeout() {
            if (this.stopScrollingTimeout) {
                clearTimeout(this.stopScrollingTimeout);
            }
        },
        getTrack() {
            return this.$refs.track;
        },
        getSlides() {
            const track = this.getTrack();
            return track ? Array.from(track?.children) : [];
        },
        getSlideByIndex(index) {
            const slides = this.getSlides();
            return slides[index];
        },
        goToSlide(index, behavior = "smooth") {
            const slide = this.getSlideByIndex(index);

            if (!slide) {
                return;
            }

            scrollLeft(this.getTrack(), slide.offsetLeft, behavior);
        },
        handleOnTrackScroll() {
            this.isScrolling = true;

            this.clearStopScrollingTimeout();
            this.clearAutoSlideTimeout();

            this.stopScrollingTimeout = setTimeout(
                this.handleOnTrackScrollEnd.bind(this),
                150
            );
        },
        handleOnTrackScrollEnd() {
            const track = this.getTrack();
            const slides = this.getSlides();

            this.currentSlideIndex = slides.findIndex((slide) => Math.abs(track.scrollLeft - slide.offsetLeft) <= 50);

            if (this.currentSlideIndex === -1) {
                console.warn("No matching slide found for the current scroll position.");
                return;
            }

            if (this.infinite) {
                if (this.currentSlideIndex === 0) {
                    this.currentSlideIndex = slides.length - 2;
                } else if (this.currentSlideIndex === slides.length - 1) {
                    this.currentSlideIndex = 1;
                }

                if (!slides[this.currentSlideIndex]) {
                    this.currentSlideIndex = 0;
                }

                if (this.currentSlideIndex >= 0 && this.currentSlideIndex < slides.length && slides[this.currentSlideIndex]) {
                    track.scrollTo({
                        left: slides[this.currentSlideIndex].offsetLeft,
                    });
                } else {
                    console.warn("Calculated currentSlideIndex is out of bounds.");
                }
            }

            this.isScrolling = false;
            this.initAutoSlide();
        },
        handleOnControlClick(direction) {
            if (this.isScrolling) {
                return;
            }

            this.goToSlide(this.currentSlideIndex + direction);
        },
    },
    mounted() {
        this.userInteractionTracker.listen(() => {
            this.initInfiniteSlide();
            this.numberOfSlides = this.getSlides().length ?? 0;
            this.initAutoSlide();
        });
    },
    watch: {
        infinite() {
            this.initInfiniteSlide();
        },
        auto() {
            this.initAutoSlide();
        }
    },
    unmounted() {
        this.clearStopScrollingTimeout();
        this.clearAutoSlideTimeout();
        this.userInteractionTracker.clear();
    }
}
</script>

<template>
    <div class="container skokka-carousel">
        <skokka-carousel-controls @click="handleOnControlClick" v-if="numberOfSlides > 1" />
        <div class="carousel-track" :class="trackClassName" ref="track" @scroll="handleOnTrackScroll">
            <slot></slot>
        </div>
    </div>
</template>